/**
 * ExportButton — one control for every download in the app (FS-023).
 *
 * Replaces six bare `<a href="/api/v1/...">` links, none of which worked: the
 * access token is attached by an axios interceptor, and a browser navigation
 * carries no header, so every one of them returned 401. See `useDownload`.
 *
 * The format choice lives here rather than in each page because the backend now
 * offers CSV and XLSX on the same endpoint, and a dealer who wants the XLSX
 * wants it everywhere, not only on the page where somebody remembered to add
 * the second link.
 */

import { clsx } from "clsx";
import { Check, ChevronDown, Download, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useDownload } from "@/hooks/useDownload";
import Button from "./Button";

export interface ExportButtonProps {
  /** API path, e.g. `/api/v1/data/export/assets`. */
  url: string;
  /** Extra query parameters sent with the request. */
  params?: Record<string, string | number | boolean | undefined>;
  /** Used only if the server sends no `Content-Disposition`. */
  fallbackFilename?: string;
  label?: string;
  variant?: "primary" | "secondary" | "ghost";
  size?: "sm" | "md";
  /** False gives a single CSV button with no menu. */
  offerFormats?: boolean;
  className?: string;
}

const FORMATS: { value: "csv" | "xlsx"; label: string; hint: string }[] = [
  { value: "csv", label: "CSV", hint: "Re-importable, opens anywhere" },
  { value: "xlsx", label: "Excel", hint: "Formatted, with a cover sheet" },
];

export function ExportButton({
  url,
  params,
  fallbackFilename = "export",
  label = "Export",
  variant = "secondary",
  size = "sm",
  offerFormats = true,
  className,
}: ExportButtonProps) {
  const { download, isDownloading, activeUrl } = useDownload();
  const [open, setOpen] = useState(false);
  const [lastFormat, setLastFormat] = useState<"csv" | "xlsx">("csv");
  const container = useRef<HTMLDivElement>(null);
  const busy = isDownloading && activeUrl === url;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const run = async (format: "csv" | "xlsx") => {
    setOpen(false);
    setLastFormat(format);
    await download(url, { params: { ...params, format }, fallbackFilename });
  };

  if (!offerFormats) {
    return (
      <Button
        variant={variant}
        size={size}
        className={className}
        disabled={busy}
        onClick={() => run("csv")}
        aria-label={`${label} as CSV`}
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Download className="h-3.5 w-3.5" />
        )}
        {label}
      </Button>
    );
  }

  return (
    <div ref={container} className={clsx("relative inline-flex", className)}>
      <Button
        variant={variant}
        size={size}
        disabled={busy}
        onClick={() => setOpen((previous) => !previous)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Download className="h-3.5 w-3.5" />
        )}
        {label}
        <ChevronDown
          className={clsx("h-3 w-3 transition-transform", open && "rotate-180")}
        />
      </Button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 w-56 overflow-hidden rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] shadow-[var(--shadow-lg)]"
        >
          {FORMATS.map((format) => (
            <button
              key={format.value}
              role="menuitem"
              type="button"
              onClick={() => run(format.value)}
              className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-[var(--surface-raised)] focus-visible:bg-[var(--surface-raised)] focus-visible:outline-none"
            >
              <span className="mt-0.5 w-3.5 shrink-0">
                {lastFormat === format.value && (
                  <Check className="h-3.5 w-3.5 text-[var(--color-interactive-primary)]" />
                )}
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-medium text-[var(--text-primary)]">
                  {format.label}
                </span>
                <span className="block text-[11px] leading-tight text-[var(--text-secondary)]">
                  {format.hint}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ExportButton;
